import { useCallback, useEffect, useMemo, useState } from "react";
import { useArtistScope } from "@/lib/artistContext";
import type { BankAccount, BankTransaction } from "@/types/bank";
import {
  createBankAccount,
  createBankRule,
  getBankAccounts,
  getBankRules,
  getBankTransactions,
  importBankStatement,
  matchBankTransaction,
  reconcileBankTransaction,
  splitBankTransaction,
} from "@/api/bank";
import type { BankRule } from "@/types/bank";

export function useBanking() {
  const { selectedArtistId } = useArtistScope();
  const [accounts, setAccounts] = useState<BankAccount[]>([]);
  const [transactions, setTransactions] = useState<BankTransaction[]>([]);
  const [selectedAccountId, setSelectedAccountId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [importing, setImporting] = useState(false);
  const [savingAccount, setSavingAccount] = useState(false);
  const [reconcilingId, setReconcilingId] = useState<string | null>(null);
  const [rules, setRules] = useState<BankRule[]>([]);
  const [savingRule, setSavingRule] = useState(false);

  const resetState = useCallback(() => {
    setAccounts([]);
    setTransactions([]);
    setSelectedAccountId(null);
    setError(null);
  }, []);

  const loadAccounts = useCallback(async () => {
    if (!selectedArtistId) {
      resetState();
      return;
    }
    try {
      const payload = await getBankAccounts();
      setAccounts(payload ?? []);
      setSelectedAccountId((prev) => {
        if (prev) {
          return payload.find((acct) => acct.id === prev) ? prev : payload[0]?.id ?? null;
        }
        return payload[0]?.id ?? null;
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load bank accounts");
    }
  }, [selectedArtistId, resetState]);

  const loadTransactions = useCallback(async () => {
    if (!selectedArtistId) {
      setTransactions([]);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const payload = await getBankTransactions({ status: "unmatched", account_id: selectedAccountId ?? undefined, limit: 100 });
      setTransactions(payload ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load transactions");
    } finally {
      setLoading(false);
    }
  }, [selectedArtistId, selectedAccountId]);

  const loadRules = useCallback(async () => {
    if (!selectedArtistId) {
      setRules([]);
      return;
    }
    try {
      const payload = await getBankRules();
      setRules(payload ?? []);
    } catch (err) {
      setError((err as Error).message ?? "Failed to load bank rules");
    }
  }, [selectedArtistId]);

  useEffect(() => {
    void loadAccounts();
    void loadRules();
  }, [loadAccounts, loadRules]);

  useEffect(() => {
    void loadTransactions();
  }, [loadTransactions]);

  const refresh = useCallback(async () => {
    await Promise.all([loadAccounts(), loadTransactions(), loadRules()]);
  }, [loadAccounts, loadTransactions, loadRules]);

  const addAccount = useCallback(
    async (payload: Parameters<typeof createBankAccount>[0]) => {
      setSavingAccount(true);
      try {
        await createBankAccount(payload);
        await loadAccounts();
      } finally {
        setSavingAccount(false);
      }
    },
    [loadAccounts],
  );

  const importStatementFile = useCallback(
    async (accountId: number, file: File) => {
      setImporting(true);
      try {
        await importBankStatement(accountId, file);
        await loadTransactions();
      } finally {
        setImporting(false);
      }
    },
    [loadTransactions],
  );

  const reconcileTransaction = useCallback(
    async (payload: Parameters<typeof reconcileBankTransaction>[0]) => {
      setReconcilingId(payload.transaction_id);
      try {
        await reconcileBankTransaction(payload);
        await loadTransactions();
      } finally {
        setReconcilingId(null);
      }
    },
    [loadTransactions],
  );

  const matchTransaction = useCallback(
    async (transactionId: string, entryId: string) => {
      setReconcilingId(transactionId);
      try {
        await matchBankTransaction(transactionId, entryId);
        await loadTransactions();
      } finally {
        setReconcilingId(null);
      }
    },
    [loadTransactions],
  );

  const splitTransaction = useCallback(
    async (payload: Parameters<typeof splitBankTransaction>[0]) => {
      setReconcilingId(payload.transaction_id);
      try {
        await splitBankTransaction(payload);
        await loadTransactions();
      } finally {
        setReconcilingId(null);
      }
    },
    [loadTransactions],
  );

  const addRule = useCallback(
    async (payload: Parameters<typeof createBankRule>[0]) => {
      setSavingRule(true);
      try {
        await createBankRule(payload);
        await loadRules();
      } finally {
        setSavingRule(false);
      }
    },
    [loadRules],
  );

  return useMemo(
    () => ({
      accounts,
      transactions,
      selectedAccountId,
      setSelectedAccountId,
      loading,
      error,
      importing,
      savingAccount,
      reconcilingId,
      rules,
      savingRule,
      refresh,
      addAccount,
      importStatementFile,
      reconcileTransaction,
      matchTransaction,
      splitTransaction,
      addRule,
    }),
    [
      accounts,
      transactions,
      selectedAccountId,
      loading,
      error,
      importing,
      savingAccount,
      reconcilingId,
      rules,
      savingRule,
      refresh,
      addAccount,
      importStatementFile,
      reconcileTransaction,
      matchTransaction,
      splitTransaction,
      addRule,
    ],
  );
}
